import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
  Tab,
  Tabs,
  Badge,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Circle as UnreadIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkReadIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { ISystemNotification } from '../../../types/interfaces/INotifications';
import { useNotificationManager } from '../../../hooks';
import { useNotificationsModalHandlers } from '../../../handlers/components/modalHandlers/notificationsModalHandlers';

interface NotificationsModalProps {
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const NotificationsModal: React.FC<NotificationsModalProps> = ({
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  // Usar la nueva capa intermedia
  const {
    notifications,
    loading,
    refreshNotifications,
    handleMarkAsRead,
    handleMarkAsUnread,
    handleMarkAllAsRead,
    handleDeleteNotification,
  } = useNotificationManager();
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (open) {
      refreshNotifications();
    }
  }, [open]); // Remover refreshNotifications de dependencias para evitar loops

  // Usar handlers extraídos
  const {
    handleTabChange,
    onMarkAsRead,
    onMarkAsUnread,
    onDeleteNotification,
    onMarkAllAsRead,
    getFilteredNotifications,
  } = useNotificationsModalHandlers({
    setTabValue,
    handleMarkAsRead,
    handleMarkAsUnread,
    handleDeleteNotification,
    handleMarkAllAsRead,
    tabValue,
    notifications,
  });

  const getNotificationIcon = (type: ISystemNotification['type']) => {
    switch (type) {
      case 'appointment':
        return <EventIcon />;
      case 'patient':
        return <PersonIcon />;
      case 'system':
        return <SettingsIcon />;
      case 'reminder':
        return <NotificationsIcon />;
      case 'alert':
        return <WarningIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getPriorityColor = (priority: ISystemNotification['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const actionRequiredCount = notifications.filter(
    n => n.actionRequired && !n.read
  ).length;
  const priorityCount = notifications.filter(
    n => (n.priority === 'urgent' || n.priority === 'high') && !n.read
  ).length;

  const handleOpenActionUrl = (actionUrl: string) => {
    if (!actionUrl) {
      return;
    }

    if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
      window.open(actionUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(actionUrl);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <NotificationsIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h5" fontWeight={600}>
                Notificaciones
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onMarkAllAsRead}
                  startIcon={<MarkReadIcon />}
                >
                  Marcar todas como leídas
                </Button>
              )}
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Todas
                    <Chip size="small" label={notifications.length} />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    No leídas
                    {unreadCount > 0 && (
                      <Badge badgeContent={unreadCount} color="error">
                        <Box />
                      </Badge>
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Acción requerida
                    {actionRequiredCount > 0 && (
                      <Badge badgeContent={actionRequiredCount} color="warning">
                        <Box />
                      </Badge>
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Prioritarias
                    {priorityCount > 0 && (
                      <Badge badgeContent={priorityCount} color="error">
                        <Box />
                      </Badge>
                    )}
                  </Box>
                }
              />
            </Tabs>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TabPanel value={tabValue} index={tabValue}>
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {getFilteredNotifications().map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: notification.read
                          ? 'transparent'
                          : 'action.hover',
                        '&:hover': { bgcolor: 'action.selected' },
                      }}
                    >
                      <ListItemIcon>
                        <Box sx={{ position: 'relative' }}>
                          {getNotificationIcon(notification.type)}
                          {!notification.read && (
                            <UnreadIcon
                              sx={{
                                position: 'absolute',
                                top: -4,
                                right: -4,
                                fontSize: 12,
                                color: 'primary.main',
                              }}
                            />
                          )}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight={notification.read ? 400 : 600}
                            >
                              {notification.title}
                            </Typography>
                            <Chip
                              size="small"
                              label={notification.priority}
                              color={
                                getPriorityColor(notification.priority) as any
                              }
                              variant="outlined"
                            />
                            {notification.actionRequired && (
                              <Chip
                                size="small"
                                label="Acción requerida"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 0.5 }}
                            >
                              {notification.message}
                            </Typography>
                            {notification.actionUrl && (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleOpenActionUrl(notification.actionUrl!)}
                                startIcon={<OpenInNewIcon fontSize="small" />}
                                sx={{ px: 0, minWidth: 'auto' }}
                              >
                                Abrir accion
                              </Button>
                            )}
                            <Typography variant="caption" color="text.disabled">
                              {format(
                                new Date(notification.createdAt),
                                'PPP p',
                                { locale: es }
                              )}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {notification.read ? (
                            <IconButton
                              size="small"
                              onClick={() => onMarkAsUnread(notification.id)}
                              title="Marcar como no leída"
                            >
                              <UnreadIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => onMarkAsRead(notification.id)}
                              title="Marcar como leída"
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() =>
                              onDeleteNotification(notification.id)
                            }
                            title="Eliminar"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < getFilteredNotifications().length - 1 && (
                      <Divider />
                    )}
                  </React.Fragment>
                ))}
                {getFilteredNotifications().length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <NotificationsIcon
                      sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No hay notificaciones
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                      {tabValue === 1 &&
                        'Todas las notificaciones han sido leídas'}
                      {tabValue === 2 &&
                        'No hay notificaciones que requieran acción'}
                      {tabValue === 3 && 'No hay notificaciones prioritarias'}
                      {tabValue === 0 &&
                        'No tienes notificaciones en este momento'}
                    </Typography>
                  </Box>
                )}
              </List>
            </TabPanel>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={onClose} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
};

export default NotificationsModal;
